import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useMedia, useTitle } from 'react-use';

import { BUTTON_TYPES, media } from '@savgroup-front-common/constants';
import { computeOrderDetailsProductWeight } from '@savgroup-front-common/core/api/Carrier/getPickupPointByGeoCoordQuery/helpers/computeOrderDetailsProductWeight';
import { Button } from '@savgroup-front-common/core/atoms/button';
import SwipeableDrawer, {
  DRAWER_SIZE,
} from '@savgroup-front-common/core/atoms/SwipeableDrawer/SwipeableDrawer';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import {
  buildPageTitle,
  SUPPORTED_ICON_POSITIONS,
} from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { OrderProductSummary, PickupPoint } from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import useGetPickupPoints from '@/view/app/hooks/useGetPickupPoints';
import { ResumeCard } from '@/view/components/ResumeCard';
import { ResumeCardButton } from '@/view/components/ResumeCardButton';

import { STEP_CONFIG } from '../../../app/NewLayout/ClassiqueRoutes.config';
import {
  NewStepLayout,
  PageFooterContainer,
} from '../../../app/NewLayout/NewStepLayout';
import { AddressManagement } from '../../../components/AddressManagement';
import HandlingCard from '../../../components/HandlingCard';
import PickupPointSelector from '../../../components/PickupPointSelector';
import { ResumeDeposit } from '../../../components/ResumeDeposit';
import { ResumeSolution } from '../../../components/ResumeSolution';
import { IrshStepValues } from '../IrshPages.types';

import messages from './messages';
import useClaimGroupDeliveryPage from './NewClaimGroupDeliveryPage.hooks';
import {
  $DeliveryItemWrapper,
  $DeliveryResultWrapper,
  $DeliveryWrapper,
  $Heading,
  $HeadingIcon,
  $LeftColumn,
} from './NewClaimGroupDeliveryPage.styles';

export const NewClaimGroupDeliveryPage: React.FC<
  RoutedStepProps<IrshStepValues>
> = ({ onNextStep, onPreviousStep }) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const {
    claimGroupConfirmation,
    groupedCarriers,
    claimGroupId,
    formContext,
    setChangedValue,
    changeSelection,
    onSubmit,
    isDeliveryHandlingLoading,
    isLoadingSubmit,
    chooseHandlingSelected,
    handlingSelected,
    homeSelectedAddressChanged,
    openModalRelayPointDeposit,
    openModalHomeDeposit,
    handleOpenDeliveryModal,
  } = useClaimGroupDeliveryPage({ onNextStep });

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();
  const handling = values?.handling;

  const hasDeposit = handling?.hasDeposit;

  const {
    setValue,
    formState: { isValid },
  } = formContext;

  const products = values.claims?.reduce<OrderProductSummary[]>(
    (acc, claim) => {
      const orderProduct = values.order?.products.find(
        (product) => product.ownerProductId === claim.ownerProductId,
      );

      if (orderProduct) {
        return [...acc, orderProduct];
      }

      return acc;
    },
    [],
  );
  const weight = computeOrderDetailsProductWeight({
    products,
  });

  const {
    sellerId,
    carriers,
    addressSelected,
    pickupPoints,
    noPickupPoints,
    addresses,
    handleSetAddressSelected,
    selectedPickupPoint,
    pickupPointsIsLoading,
  } = useGetPickupPoints({
    selectedCarrierType: chooseHandlingSelected,
    groupedCarriers,
    claimGroupId,
    weight,
  });

  const handleSetSelectedPickupPoint = (pickupPointSelected: PickupPoint) => {
    Promise.resolve(setValue('pickupPointSelected', pickupPointSelected)).then(
      () => onSubmit(),
    );
  };

  useTitle(buildPageTitle([messages.claimGroupDeliveryTitlePage]));

  return (
    <FormProvider {...formContext}>
      <form onSubmit={onSubmit}>
        <NewStepLayout stepName={STEP_CONFIG.CLAIM_GROUP_DELIVERY}>
          <$DeliveryWrapper data-testid="DeliveryWrapper">
            <$LeftColumn>
              {claimGroupId && (
                <ResumeSolution
                  claimGroupId={claimGroupId}
                  claimGroupConfirmation={claimGroupConfirmation}
                />
              )}
              {hasDeposit && (
                <ResumeDeposit
                  claimGroupConfirmation={claimGroupConfirmation}
                />
              )}
              <$Heading data-testid="handling-delivery-title">
                <$HeadingIcon data-testid="ClaimGroupDeliveryHeadingIcon">
                  <MyAccountIcon icon={ICONS_TYPE.DELIVERY_ICON} />
                </$HeadingIcon>
                <SafeFormattedMessageWithoutSpread
                  message={messages.handlingTitle}
                />
              </$Heading>
              <$DeliveryItemWrapper data-testid="DeliveryItemWrapper">
                {isDeliveryHandlingLoading && <BaseLoader />}

                {!isDeliveryHandlingLoading && (
                  <HandlingCard
                    groupedCarriers={groupedCarriers}
                    setChangedValue={setChangedValue}
                    handleOpenModal={handleOpenDeliveryModal}
                  />
                )}
              </$DeliveryItemWrapper>
            </$LeftColumn>
            <$DeliveryResultWrapper>
              {isMobileView && (
                <ResumeCardButton
                  claimGroupConfirmation={claimGroupConfirmation}
                  hasPrice
                  hasDepositPrice
                />
              )}
              {!isMobileView && (
                <ResumeCard
                  claimGroupConfirmation={claimGroupConfirmation}
                  hasPrice
                  hasDepositPrice
                />
              )}
            </$DeliveryResultWrapper>
          </$DeliveryWrapper>
        </NewStepLayout>

        <PageFooterContainer>
          <Button
            primary
            type={BUTTON_TYPES.BUTTON}
            icon={<ArrowNakedRightIcon />}
            position={SUPPORTED_ICON_POSITIONS.RIGHT}
            disabled={!isValid}
            isLoading={isLoadingSubmit}
            onClick={onSubmit}
            dataTestId="nextButton"
          >
            <SafeFormattedMessageWithoutSpread message={messages.nextButton} />
          </Button>

          <Button
            type={BUTTON_TYPES.BUTTON}
            tertiary
            onClick={() => {
              onPreviousStep({});
            }}
            icon={<ArrowNakedLeftIcon />}
            position={SUPPORTED_ICON_POSITIONS.LEFT}
            dataTestId="previousButton"
          >
            <SafeFormattedMessageWithoutSpread
              message={messages.previousButton}
            />
          </Button>
        </PageFooterContainer>

        <SwipeableDrawer
          isOpen={handlingSelected || openModalRelayPointDeposit}
          onClose={changeSelection}
          hasBackdrop
          hasPadding={false}
          hasBackdropScroll={false}
        >
          {handlingSelected && (
            <PickupPointSelector
              maxDistance={200}
              pickupPoints={pickupPoints}
              selectedPickupPointId={
                selectedPickupPoint ? selectedPickupPoint.id : undefined
              }
              onSelectPickupPoint={handleSetSelectedPickupPoint}
              searchAddress={addressSelected}
              userAddresses={addresses}
              onAddressSelected={handleSetAddressSelected}
              carriers={carriers}
              pickupPointsIsLoading={pickupPointsIsLoading}
              sellerId={sellerId}
              noPickupPoints={noPickupPoints}
            />
          )}
        </SwipeableDrawer>

        <SwipeableDrawer
          isOpen={homeSelectedAddressChanged || openModalHomeDeposit}
          size={DRAWER_SIZE.SMALL}
          onClose={changeSelection}
          hasBackdrop
          hasBackdropScroll={false}
        >
          <AddressManagement onSubmit={onSubmit} />
        </SwipeableDrawer>
      </form>
    </FormProvider>
  );
};
